import React, { useCallback, useContext, useMemo } from "react";
import { CheckboxField, HeaderB } from "components";
import lang from "translations";
import { Path } from "paths";
import { Transfer } from "antd";
import { FragmentA } from "components/fragments";
import {
    Field,
    Input,
    Select,
    Panel,
    ActionButton,
    Toast,
    Form,
    Skeleton,
} from "components/commons";
import { searchLocation } from "apis/location.api";
import { searchResource } from "apis/resource.api";
import { useEffect, useState } from "react";
import { ModuleWrapper } from "components/fragments";
import { useForm, useModal, useRouter } from "hooks";
import { VenueContext } from "contexts";
import sectionFormState from "./section.form-state";

const SectionForm = ({
    submit,
    title,
    submitting,
    initialData = undefined,
    deleteSectionButton,
    loading,
    error,
    sectionId
}) => {
    const { venue } = useContext(VenueContext);
    const { history } = useRouter();
    const { venueId } = venue;


    const formState = useMemo(() => sectionFormState(initialData), [initialData]);
    const unsaveChangesModal = useModal();
    const {
        fields,
        modifyField,
        submitForm,
        getFormValues,
        applyFieldErrors,
        dirty,
    } = useForm({ initialState: formState });


    // Location dropdown state
    const [locationOptions, setLocationOptions] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState(false);

    // Resource mapping state
    const [allResources, setAllResources] = useState([]);
    const [mappedResourceIds, setMappedResourceIds] = useState([]);
    const [loadingResources, setLoadingResources] = useState(false);

    useEffect(() => {
        async function fetchLocations() {
            setLoadingLocations(true);
            try {
                // Fetch all locations (no filter, or add venueId if needed)
                const res = await searchLocation({
                    venueId,
                    page: 1,
                    pageSize: 200,
                });
                // Map locations to options (text, value) as in supply item form
                const options = (res.data || [])
                    .filter(loc => (loc.locationId || loc.id) && (loc.locationName || loc.name))
                    .map(loc => ({
                        text: String(loc.locationName || loc.name),
                        value: String(loc.locationId || loc.id),
                    }));
                setLocationOptions(options);
            } catch (e) {
                setLocationOptions([]);
            } finally {
                setLoadingLocations(false);
            }
        }
        fetchLocations();
    }, [venueId]);

    // Fetch all resources and mapped resources for this section (zone)
    useEffect(() => {
        async function fetchResources() {
            setLoadingResources(true);
            try {
                // Fetch all resources
                const allRes = await searchResource({ venueId, page: 1, pageSize: 500 });
                const allList = (allRes.data?.data || []).map(r => ({
                    key: String(r.resourceId),
                    title: r.defaultName || r.customName || `Resource ${r.resourceId}`,
                }));
                setAllResources(allList);

                // Fetch mapped resources for this section (zoneId = sectionId)
                if (sectionId) {
                    const mappedRes = await searchResource({ venueId, zoneId: sectionId, page: 1, pageSize: 500 });
                    const mappedIds = (mappedRes.data?.data || []).map(r => String(r.resourceId));
                    setMappedResourceIds(mappedIds);
                } else {
                    setMappedResourceIds([]);
                }
            } catch (e) {
                setAllResources([]);
                setMappedResourceIds([]);
            } finally {
                setLoadingResources(false);
            }
        }
        fetchResources();
    }, [venueId, initialData, sectionId]);

    // Navigate Back
    const goToList = useCallback(() => {
        history.push(Path.SECTION);
    }, [history]);

    // Leave Page
    const leavePage = useCallback(() => {
        if (dirty) {
            unsaveChangesModal.show({
                ok: () => {
                    goToList();
                    unsaveChangesModal.close();
                },
            });
            return;
        }
        goToList();
    }, [dirty, unsaveChangesModal, goToList]);

    // Submit Handler
    const handleSubmit = useCallback(async () => {
        const params = getFormValues();
        const obj = {
            name: params.name,
            locationId: params.locationId,
            isActive: params.isActive,
            resourceIds: mappedResourceIds, // include mapped resource ids
        };
        try {
            const res = await submit({ ...obj, venueId });
            Toast({
                content: res.message || lang.sectionSaved,
                success: true,
                icon: "check",
            }).open();
            history.push(Path.SECTION);
        } catch ({ code, handleError }) {
            if (code === 3003) {
                applyFieldErrors({ name: lang.sectionNameAlreadyExist });
            } else {
                handleError && handleError();
            }
        }
    }, [submit, getFormValues, venueId, history, applyFieldErrors, mappedResourceIds]);

    // Submit Trigger
    const submitFormValue = () => {
        submitForm(handleSubmit);
    };


    return (
        <ModuleWrapper
            error={error}
            header={
                <HeaderB
                    title={title}
                    returnText={lang.section}
                    onClick={leavePage}
                />
            }
        >
            <Form
                unsaveChangesModal={unsaveChangesModal}
                onSubmit={submitFormValue}
                isPrompt={dirty}
            >
                <FragmentA title={lang.basicInfo}>
                    <Panel>
                        {loading ? (
                            <Skeleton />
                        ) : (
                            <div>
                                {/* Name */}
                                <Field label={fields.name.label} {...fields.name}>
                                    <Input required {...fields.name} onChange={modifyField} />
                                </Field>
                                {/* Location Dropdown */}
                                <Field label={fields.locationId.label} {...fields.locationId}>
                                    <Select
                                        {...fields.locationId}
                                        options={locationOptions}
                                        onChange={modifyField}
                                        loading={loadingLocations}
                                    />
                                </Field>
                                {/* Active */}
                                <Field label={fields.isActive.label} {...fields.isActive}>
                                    <CheckboxField {...fields.isActive} onChange={modifyField} />
                                </Field>
                            </div>
                        )}
                    </Panel>
                </FragmentA>
                {/* Resource Mapping Section */}
                <FragmentA title="Resource Mapping">
                    <Panel>
                        <Transfer
                            dataSource={allResources}
                            titles={["All Resources", "Mapped Resources"]}
                            targetKeys={mappedResourceIds}
                            onChange={setMappedResourceIds}
                            render={item => item.title}
                            rowKey={item => item.key}
                            loading={loadingResources}
                            listStyle={{ width: 250, height: 300 }}
                        />
                    </Panel>
                </FragmentA>
                {/* Buttons */}
                <ActionButton
                    showLine
                    loading={submitting}
                    primary={{ onClick: submitFormValue }}
                    secondary={{ onClick: leavePage }}
                    danger={deleteSectionButton}
                />
            </Form>
        </ModuleWrapper>
    );
};

export default SectionForm;